import { Box, CircularProgress } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { CAMPAIGN_ITEMS_PER_PAGE_WHEN_SCROLLED_INFINITELY } from '../../../constants/campaigns';
import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import useElementScrollTracker from '../../../hooks/useElementScrollTracker';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';
import { useCampaignsInfiniteScroll } from '../../../service/InventoryService';
import { ICampaign, ICampaignSummary } from '../../../types/campaigns';
import { isCampaignEligibleToOrder } from '../../../utils/campaigns';
import { isSameFC } from '../../../utils/inventories';
import BoxItem from '../../BoxItem/BoxItem';
import { FormControls } from '../../SummaryComponentMUI/components/common';
import { SearchInput } from '../_controls_/SearchInput';
import { AddSection } from './AddSection';

import styles from '../../BoxList/BoxList.module.scss';

interface IProps {
  onClose: () => void;
  value: ICampaignSummary[] | null | undefined;
  onSubmit: (campaigns: ICampaignSummary[]) => void;
}

const toMap = (campaigns: ICampaignSummary[] | null | undefined): Record<string, ICampaignSummary> =>
  (campaigns || []).reduce<Record<string, ICampaignSummary>>((acc, campaign) => {
    acc[campaign.box_id] = campaign;
    return acc;
  }, {});

export const AddCampaigns: React.FC<IProps> = ({ onClose, value, onSubmit }) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState(input);
  const [selectedCampaignsMap, setSelectedCampaignsMap] = useState<Record<string, ICampaignSummary>>(() =>
    toMap(value),
  );

  const containerRef = React.useRef(null);

  const {
    data: campaignData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchedAfterMount,
  } = useCampaignsInfiniteScroll({
    page_size: CAMPAIGN_ITEMS_PER_PAGE_WHEN_SCROLLED_INFINITELY,
    search_query: query,
  });

  const campaignsList = useMemo<ICampaign[]>(() => (campaignData?.pages.flat() as ICampaign[]) || [], [campaignData]);

  const handleScroll = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    threshold: 0.75,
  });

  // Since next page fetch is triggered by the scroll event we need this hook
  // to track whether it's enough campaigns to fulfill the height of the dropdown and cause scroll
  // if there's still a page to fetch and there's no scroll on the UI, it will fetch the next page
  useElementScrollTracker({
    trigger: isFetchedAfterMount && !!campaignsList.length && hasNextPage && !isFetching,
    ref: containerRef,
    callback: fetchNextPage,
  });

  const debouncedSetQuery = useDebouncedCallback((v: string) => {
    setQuery(v);
  }, 500);

  const onChange = useCallback(
    (v: string) => {
      setInput(v);
      debouncedSetQuery(v);
    },
    [debouncedSetQuery],
  );

  const toggleSelection = useCallback((campaign: ICampaign) => {
    const id = campaign.box_id;
    setSelectedCampaignsMap((prev) => {
      if (prev[id]) {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: campaign as ICampaignSummary };
    });
  }, []);

  const handleCancel = useCallback(() => {
    setSelectedCampaignsMap(toMap(value));
    onClose();
  }, [onClose, value]);

  const selectedCampaigns = useMemo(() => Object.values(selectedCampaignsMap), [selectedCampaignsMap]);

  const handleSubmit = useCallback(() => {
    onSubmit(selectedCampaigns);
    onClose();
  }, [selectedCampaigns, onClose, onSubmit]);

  const isFirstItemsFetching = useMemo(() => {
    return isFetching && !isFetchingNextPage && campaignsList.length === 0;
  }, [isFetching, isFetchingNextPage, campaignsList]);

  const renderBoxItem = useCallback(
    (box: ICampaign) => {
      const { box_id: boxId, items } = box;
      const isBoxDisabled = !isSameFC(items) || !isCampaignEligibleToOrder(items);

      return (
        <Box key={boxId}>
          <BoxItem
            key={boxId}
            box={box}
            onClick={() => toggleSelection(box)}
            onSelect={() => toggleSelection(box)}
            className={styles.item}
            selected={!!selectedCampaignsMap[boxId]}
            iconClassName={styles.icon}
            withPreview
            withWarning
            isDisabled={isBoxDisabled}
          />
        </Box>
      );
    },
    [selectedCampaignsMap, toggleSelection],
  );

  const isLoading = useMemo(() => {
    return isFirstItemsFetching || isFetchingNextPage;
  }, [isFirstItemsFetching, isFetchingNextPage]);

  const renderSpinner = () => {
    return (
      <Box
        sx={{
          gridColumn: '1 / -1',
          display: 'flex',
          height: '100px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  };

  return (
    <AddSection title="Add campaigns" onClose={handleCancel}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
        }}
      >
        <SearchInput value={input} onChange={(v) => onChange(v as string)} placeholderText="campaigns" />
        <Box
          sx={{
            my: 2,
            flex: '1 1 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gridAutoRows: '300px',
            gap: 1,
            overflowY: 'auto',
            minHeight: 'calc(100vh - 280px)',
            maxHeight: 'calc(100vh - 280px)',
          }}
          onScroll={handleScroll}
          ref={containerRef}
        >
          {campaignsList.map(renderBoxItem)}
          {isLoading && renderSpinner()}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mr: '-3px',
            pt: '9px',
          }}
        >
          <FormControls
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            isDisabled={!selectedCampaigns.length}
            customText="Continue"
          />
        </Box>
      </Box>
    </AddSection>
  );
};
